<script>
  import { line, curveBumpX } from 'd3-shape';
  import { partyToColour, partyToHoverColour } from '$lib/constants/index';
  import { pluckDataFromX } from '$lib/math';
  import Legend from './Legend.svelte';
  import CursorLine from './CursorLine.svelte';
  let {
    w,
    h,
    margin,
    chartwidth,
    chartheight,
    xScale,
    yScale,
    trends,
    block,
    extents,
    hoveredParty = $bindable(null),
  } = $props();

  // parties within this many px of the cursor count as "hovered"
  const HOVER_THRESHOLD = 20;
  // last-value seat threshold for a party's line to stay coloured by default
  const MAJOR_THRESHOLD = 10;

  let scrubX = $state(null);
  let scrubY = $state(null);
  let g = $state(0);

  const mousemove = (evt) => {
    scrubX = evt.offsetX;
    scrubY = evt.offsetY;
  };

  const mouseout = () => {
    scrubX = null;
    scrubY = null;
  };

  const assembleLines = () => {
    const output = {};
    if (w && xScale) {
      for (const trend of Object.keys(trends)) {
        const datum = trends[trend];
        const lineGen = line()
          .x((d) => xScale(d[0]))
          .y((d) => yScale(d[1]))
          .curve(curveBumpX)(datum);

        output[trend] = lineGen;
      }
    }
    return output;
  };

  let linesToRender = $derived(assembleLines());

  // a party's line stays coloured by default once it's at/above the seat
  // threshold as of its last available data point
  const lastValue = (trend) => {
    const datum = trends[trend];
    return datum && datum.length ? datum[datum.length - 1][1] : undefined;
  };

  const isMajorParty = (trend) => {
    const datum = trends[trend];
    if (!datum || !datum.length) return false;
    return datum[datum.length - 1][1] >= MAJOR_THRESHOLD;
  };

  // party values at the cursor's date, regardless of cursor y-position —
  // drives which lines stay coloured (not just the closest/hovered one)
  let pollDataAtCursor = $derived.by(() => {
    if (scrubX === null || !xScale) return null;
    const chartX = scrubX - margin.left;
    if (chartX < 0 || chartX > chartwidth) return null;
    return pluckDataFromX({ x: chartX, trends, xScale, extents });
  });

  const isMajorAtCursor = (trend) => {
    if (pollDataAtCursor) {
      const v = pollDataAtCursor[trend];
      return v !== null && v !== undefined && v >= MAJOR_THRESHOLD;
    }
    return isMajorParty(trend);
  };

  let computedHoveredParty = $derived.by(() => {
    if (scrubY === null || !pollDataAtCursor || !yScale) return null;
    const chartY = scrubY - margin.top;
    if (chartY < 0 || chartY > chartheight) return null;

    let closest = null;
    let closestDist = Infinity;
    for (const key of Object.keys(pollDataAtCursor)) {
      const val = pollDataAtCursor[key];
      if (val === null || val === undefined) continue;
      const dist = Math.abs(yScale(val) - chartY);
      if (dist < closestDist) {
        closestDist = dist;
        closest = key;
      }
    }
    return closestDist <= HOVER_THRESHOLD ? closest : null;
  });

  $effect(() => {
    hoveredParty = computedHoveredParty;
  });

  // render the hovered line last so it draws on top of the others
  let sortedTrendKeys = $derived(
    Object.keys(linesToRender).sort((a, b) => {
      if (a === computedHoveredParty) return 1;
      if (b === computedHoveredParty) return -1;
      return 0;
    })
  );
</script>

<svg
  width={w}
  height={h}
  class="chart-svg"
  role="figure"
  on:pointerdown={mousemove}
  on:pointermove={mousemove}
  on:pointerup={mouseout}
  on:pointercancel={mouseout}
  on:pointerleave={mouseout}
>
  <defs>
    <clipPath id="chart-clip">
      <rect
        class="chart-clip"
        width={chartwidth}
        height={chartheight}
        x={0}
        y={0}
        fill="red"
      ></rect>
    </clipPath>
  </defs>

  <g
    class="chart"
    transform="translate({margin.left}, {margin.top})"
    clip-path="url(#chart-clip)"
    bind:this={g}
  >
    {#each sortedTrendKeys as trendLine}
      {@const lineData = linesToRender[trendLine]}
      {@const hovered = trendLine === computedHoveredParty}
      {@const color = hovered ? partyToHoverColour(trendLine, lastValue(trendLine)) : partyToColour(trendLine)}
      {@const major = isMajorAtCursor(trendLine)}
      <path
        d={lineData}
        fill="none"
        stroke-width={hovered ? 2.5
        : major ? 2.5
        : 1.25}
        stroke={major || hovered ? color : '#cccccc'}
      ></path>
    {/each}
  </g>
  <CursorLine
    {chartwidth}
    {chartheight}
    {trends}
    {scrubX}
    {xScale}
    {yScale}
    {margin}
    {g}
    {extents}
    hoveredParty={computedHoveredParty}
  />
  {#if margin}
    <Legend position={[margin.left + 10, 55]} {block} />
  {/if}
</svg>

<style lang="scss">
  .chart-svg {
    cursor: crosshair;
    overflow: visible;
    // let pointer events (incl. touch) drive scrubbing directly instead of
    // fighting the browser's own scroll/zoom gestures — this is what makes
    // dragging a finger across the chart track smoothly
    touch-action: none;
  }
</style>
